import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";
import PaymentBadges from "@/components/PaymentBadges";

const PROJECT_TYPES = [
  { value: "artwork", label: "Original Artwork" },
  { value: "design", label: "Graphic Design" },
  { value: "engraving", label: "Laser Engraving" },
  { value: "3d_printing", label: "3D Printing" },
  { value: "mural", label: "Mural" },
  { value: "workshop", label: "Workshop (school / party / group)" },
  { value: "tattoo", label: "Tattoo Design" },
  { value: "custom_panel", label: "Custom Panel" },
  { value: "other", label: "Other" },
];

export default function CustomRequests() {
  const [params] = useSearchParams();
  const initialType = params.get("type") || "artwork";
  const validType = PROJECT_TYPES.some((p) => p.value === initialType) ? initialType : "artwork";
  const [form, setForm] = useState({
    name: "", email: "", phone: "", project_type: validType,
    budget: "", deadline: "", description: "", reference_url: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (params.get("type") && PROJECT_TYPES.some((p) => p.value === params.get("type"))) {
      setForm((f) => ({ ...f, project_type: params.get("type") }));
    }
  }, [params]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.description) {
      toast.error("Please fill name, email and description");
      return;
    }
    try {
      setSubmitting(true);
      await publicApi.submitRequest(form);
      toast.success("Request received — we'll be in touch!");
      setSubmitted(true);
    } catch (err) {
      toast.error("Something went wrong. Try again?");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="custom-request-success" className="max-w-[900px] mx-auto px-5 md:px-10 py-24 text-center">
        <p className="overline mb-3">Sent</p>
        <h1 className="font-display text-5xl md:text-7xl mb-6">Talk soon.</h1>
        <p className="text-white/70 mb-8">Your request landed in Cam's inbox. Expect a reply within 2 business days.</p>
        <button data-testid="custom-request-another" onClick={() => { setSubmitted(false); setForm({ ...form, name: "", email: "", description: "" }); }} className="btn-ghost">Submit another</button>
      </div>
    );
  }

  return (
    <div data-testid="page-custom-requests" className="max-w-[1000px] mx-auto px-5 md:px-10 py-16">
      <p className="overline mb-3">The Studio</p>
      <h1 className="font-display text-5xl md:text-7xl leading-none mb-4">Custom request.</h1>
      <p className="text-white/60 max-w-xl mb-12">Tell us the story. Every commission — apparel, engraving, 3D print, mural, tattoo design or panel — starts with a conversation.</p>

      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="overline block mb-2">Name*</label>
          <input data-testid="custom-input-name" required className="cka-input" value={form.name} onChange={update("name")} />
        </div>
        <div>
          <label className="overline block mb-2">Email*</label>
          <input data-testid="custom-input-email" type="email" required className="cka-input" value={form.email} onChange={update("email")} />
        </div>
        <div>
          <label className="overline block mb-2">Phone</label>
          <input data-testid="custom-input-phone" className="cka-input" value={form.phone} onChange={update("phone")} />
        </div>
        <div>
          <label className="overline block mb-2">Project type*</label>
          <select data-testid="custom-input-type" className="cka-input" value={form.project_type} onChange={update("project_type")}>
            {PROJECT_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="overline block mb-2">Budget (AUD)</label>
          <input data-testid="custom-input-budget" className="cka-input" value={form.budget} onChange={update("budget")} placeholder="e.g. $500 - $2000" />
        </div>
        <div>
          <label className="overline block mb-2">Deadline</label>
          <input data-testid="custom-input-deadline" className="cka-input" value={form.deadline} onChange={update("deadline")} placeholder="e.g. by June 2026" />
        </div>
        <div className="md:col-span-2">
          <label className="overline block mb-2">Reference image URL</label>
          <input data-testid="custom-input-reference" className="cka-input" value={form.reference_url} onChange={update("reference_url")} placeholder="https://..." />
        </div>
        <div className="md:col-span-2">
          <label className="overline block mb-2">Project description*</label>
          <textarea data-testid="custom-input-description" required rows={6} className="cka-input" value={form.description} onChange={update("description")} placeholder="Tell us about the piece — the story, materials, size, and any inspiration..." />
        </div>
        <div className="md:col-span-2 pt-4">
          <button data-testid="custom-submit" type="submit" disabled={submitting} className="btn-primary disabled:opacity-50">
            {submitting ? "Sending..." : "Send Request"}
          </button>
        </div>
      </form>
    </div>
  );
}
